/**
 * SpacesByTypePageV2 - Página de espacios por tipo CON DISPONIBILIDAD
 * Nueva versión: Fechas primero → Tipo → Solo espacios disponibles
 */
import React, { useMemo, useState, useEffect } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '../hooks/useTheme'
import { useTiposPublicos } from '../hooks/useTiposPublicos'
import { useDisponibilidadSimple } from '../hooks/useEspacios'
import { slugify } from '../utils/stringHelpers'
import { formatLocalDateToString, formatDateDisplay } from '../utils/dateHelpers'
import { filterEspaciosPublicos } from '../utils/espacioFilters'
import SimpleHeader from '../components/common/SimpleHeader'
import SpaceTypesFilterBar from '../components/spaces/SpaceTypesFilterBar'
import SpaceCardV2 from '../components/spaces/SpaceCardV2'
import LoadingSpinner from '../components/common/LoadingSpinner'
import ErrorMessage from '../components/common/ErrorMessage'
import Footer from '../components/common/Footer'
import type { FiltrosDisponibilidad } from '../types/availability'

const SpacesByTypePageV2: React.FC = () => {
  const { tipoSlug } = useParams<{ tipoSlug: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { colors } = useTheme()

  // Estado para fechas de búsqueda (inicializadas con mes actual)
  const [fechaEntrada, setFechaEntrada] = useState<string>('')
  const [fechaSalida, setFechaSalida] = useState<string>('')

  // Scroll al inicio cuando se monta el componente
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  // Inicializar fechas con inicio y fin del mes actual
  useEffect(() => {
    const hoy = new Date()
    const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
    const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)

    setFechaEntrada(formatLocalDateToString(primerDia))
    setFechaSalida(formatLocalDateToString(ultimoDia))
  }, [])

  const { data: tipos = [], isLoading: loadingTipos, error: errorTipos } = useTiposPublicos()

  // Filtrar tipos reservables para el filter bar
  const tiposReservables = useMemo(() =>
    tipos.filter(t => t.reservable && !t.privado),
    [tipos]
  )

  // Encontrar tipo actual
  const tipoActual = useMemo(() =>
    tipos.find(t => slugify(t.nombre) === tipoSlug),
    [tipos, tipoSlug]
  )

  // ✅ Consultar disponibilidad solo cuando hay fechas válidas
  const filtrosDisponibilidad = useMemo<FiltrosDisponibilidad | undefined>(() => {
    if (!fechaEntrada || !fechaSalida) return undefined
    if (!tipoActual?.id) return undefined

    // ✅ Validar que fecha_entrada <= fecha_salida antes de consultar
    if (fechaEntrada > fechaSalida) return undefined

    return {
      fecha_entrada: fechaEntrada,
      fecha_salida: fechaSalida,
      tipo_espacio_id: tipoActual.id
    }
  }, [fechaEntrada, fechaSalida, tipoActual?.id])

  // ✅ Hook de disponibilidad simple (solo espacios disponibles, sin tarifas)
  const {
    data: espaciosDisponiblesRaw = [],
    isLoading: loadingDisponibilidad,
    error: errorDisponibilidad
  } = useDisponibilidadSimple(filtrosDisponibilidad)

  // ✅ Filtrar espacios privados
  const espaciosDisponibles = useMemo(
    () => filterEspaciosPublicos(espaciosDisponiblesRaw),
    [espaciosDisponiblesRaw]
  )

  const isLoading = loadingTipos || loadingDisponibilidad
  const error = errorTipos || errorDisponibilidad

  const handleViewDetails = (espacioId: string) => {
    // Navegar con fechas si están seleccionadas
    navigate(`/reservar/${tipoSlug}/${espacioId}`, {
      state: {
        fechaEntrada,
        fechaSalida
      }
    })
  }

  const handleSelectTipo = (slug: string) => {
    navigate(`/reservar/${slug}`)
  }

  const handleLimpiarFechas = () => {
    const hoy = new Date()
    const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
    const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)
    setFechaEntrada(formatLocalDateToString(primerDia))
    setFechaSalida(formatLocalDateToString(ultimoDia))
  }

  if (isLoading && !fechaEntrada) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <ErrorMessage message="Error al cargar los espacios" />
      </div>
    )
  }

  if (!tipoActual) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-6xl mb-4">❓</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Tipo de espacio no encontrado
          </h2>
          <p className="text-gray-600 mb-6">
            El tipo "{tipoSlug}" no existe o no está disponible
          </p>
          <button
            onClick={() => navigate('/reservar')}
            className="px-6 py-3 rounded-lg text-white font-semibold"
            style={{ backgroundColor: colors.primary }}
          >
            Ver todos los tipos
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SimpleHeader
        title={`Disponibilidad: ${tipoActual.nombre}`}
        subtitle={fechaEntrada && fechaSalida
          ? `${formatDateDisplay(fechaEntrada)} - ${formatDateDisplay(fechaSalida)}`
          : 'Selecciona fechas para ver disponibilidad'
        }
      />

      {/* 1. SELECTOR DE FECHAS - Sticky y prominente */}
      <div className="sticky top-0 z-40 bg-white border-b-2 shadow-md" style={{ borderColor: colors.primary }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">📅 Rango de Fechas</h3>
              <span className="text-xs text-gray-500">
                {espaciosDisponibles.length} {espaciosDisponibles.length === 1 ? 'espacio' : 'espacios'} disponible{espaciosDisponibles.length === 1 ? '' : 's'}
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <div className="flex items-center gap-2 flex-1">
                <label htmlFor="fecha-entrada" className="text-sm font-medium text-gray-700 whitespace-nowrap min-w-[70px]">
                  Entrada:
                </label>
                <input
                  id="fecha-entrada"
                  type="date"
                  value={fechaEntrada}
                  onChange={(e) => setFechaEntrada(e.target.value)}
                  className="flex-1 px-3 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:border-camping-500 focus:ring-2 focus:ring-camping-200"
                />
              </div>

              <div className="flex items-center gap-2 flex-1">
                <label htmlFor="fecha-salida" className="text-sm font-medium text-gray-700 whitespace-nowrap min-w-[70px]">
                  Salida:
                </label>
                <input
                  id="fecha-salida"
                  type="date"
                  value={fechaSalida}
                  min={fechaEntrada}
                  onChange={(e) => setFechaSalida(e.target.value)}
                  className="flex-1 px-3 py-2.5 border-2 border-gray-300 rounded-lg text-sm focus:border-camping-500 focus:ring-2 focus:ring-camping-200"
                />
              </div>

              <button
                onClick={handleLimpiarFechas}
                className="px-4 py-2.5 text-sm text-gray-600 hover:text-gray-900 underline whitespace-nowrap"
              >
                🔄 Mes actual
              </button>
            </div>

            {!fechaEntrada || !fechaSalida ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  ⚠️ Selecciona fechas de entrada y salida para ver espacios disponibles
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {/* 2. FILTRO DE TIPO DE ESPACIO - Secundario */}
      <SpaceTypesFilterBar
        tipos={tiposReservables.map(t => ({ id: slugify(t.nombre), nombre: t.nombre }))}
        selectedTipoId={tipoSlug || null}
        onSelectTipo={handleSelectTipo}
      />

      {/* 3. GRID DE ESPACIOS DISPONIBLES */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {loadingDisponibilidad ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner size="lg" text="Buscando espacios disponibles..." />
          </div>
        ) : !fechaEntrada || !fechaSalida ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📅</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Selecciona tus fechas
            </h2>
            <p className="text-gray-600 mb-6">
              Elige las fechas de entrada y salida para ver los espacios disponibles
            </p>
          </div>
        ) : espaciosDisponibles.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">😔</div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              No hay espacios disponibles
            </h2>
            <p className="text-gray-600 mb-6">
              No encontramos espacios de tipo "{tipoActual.nombre}" disponibles para las fechas seleccionadas
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleLimpiarFechas}
                className="px-6 py-3 rounded-lg border-2 text-gray-700 font-semibold hover:bg-gray-50"
                style={{ borderColor: colors.primary }}
              >
                Cambiar fechas
              </button>
              <button
                onClick={() => navigate('/reservar')}
                className="px-6 py-3 rounded-lg text-white font-semibold"
                style={{ backgroundColor: colors.primary }}
              >
                Ver otros tipos
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                ✅ Mostrando <strong>{espaciosDisponibles.length}</strong> espacio{espaciosDisponibles.length === 1 ? '' : 's'}
                {' '}con disponibilidad (total o parcial) entre el {formatDateDisplay(fechaEntrada)} y {formatDateDisplay(fechaSalida)}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {espaciosDisponibles.map(espacio => (
                <SpaceCardV2
                  key={espacio.id}
                  espacio={espacio}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  )
}

export default SpacesByTypePageV2
